import { create } from 'zustand'

import { getBattlePassApi } from '@api'

import { createSelectors, Mapper } from './helpers'

export interface BattlePassLevelBackend {
  id: number
  level: number
  description?: string
  level_type: 'default' | 'premium'
  reward_meta: string
  reward_type: string
  stars: number
  icon_url: string
  name: string
}

export interface BattlePassLevel {
  id: number
  level: number
  levelType: 'default' | 'premium'
  rewardMeta: string
  rewardType: string
  stars: number
  iconUrl: string
  name: string
  description?: string
}

export interface BattlePassBackend {
  available_at: string
  created_at: string
  description: string
  finished_at: string
  id: number
  levels: BattlePassLevelBackend[]
  name: string
}

export interface BattlePass {
  availableAt: string
  createdAt: string
  description: string
  finishedAt: string
  id: number
  levels: BattlePassLevel[]
  name: string
}

const BattlePassLevelMapper: Mapper<BattlePassLevelBackend, BattlePassLevel> = {
  mapFromBackend: function (data) {
    return {
      id: data.id,
      level: data.level,
      levelType: data.level_type,
      rewardMeta: data.reward_meta,
      rewardType: data.reward_type,
      stars: data.stars,
      iconUrl: data.icon_url,
      name: data.name,
      description: data.description,
    }
  },
  mapToBackend(data) {
    return {
      id: data.id,
      level: data.level,
      level_type: data.levelType,
      reward_meta: data.rewardMeta,
      reward_type: data.rewardType,
      stars: data.stars,
      icon_url: data.iconUrl,
      name: data.name,
      description: data.description,
    }
  },
}

const BattlePassMapper: Mapper<BattlePassBackend, BattlePass> = {
  mapFromBackend: function (data) {
    return {
      availableAt: data.available_at,
      createdAt: data.created_at,
      description: data.description,
      finishedAt: data.finished_at,
      id: data.id,
      levels: data.levels.map((item) =>
        BattlePassLevelMapper.mapFromBackend(item)
      ),
      name: data.name,
    }
  },
  mapToBackend(data) {
    return {
      available_at: data.availableAt,
      created_at: data.createdAt,
      description: data.description,
      finished_at: data.finishedAt,
      id: data.id,
      levels: data.levels.map((item) =>
        BattlePassLevelMapper.mapToBackend(item)
      ),
      name: data.name,
    }
  },
}

interface BattlePassState {
  isLoading: boolean
  isError: boolean
  data: BattlePass | null
  battlePassLevels: number | null
  actions: {
    getBattlePass: () => void
  }
}

const useBattlePassStoreBase = create<BattlePassState>((set, get) => ({
  isLoading: true,
  isError: false,
  data: null,
  battlePassLevels: null,
  actions: {
    getBattlePass: async () => {
      set(() => ({ isLoading: true }))

      const { data, ok } = await getBattlePassApi()

      if (ok && data) {
        set(() => ({
          data: BattlePassMapper.mapFromBackend(data),
          battlePassLevels: data.levels.length / 2,
        }))
      }

      set(() => ({
        isLoading: false,
      }))
    },
  },
}))

export const useBattlePassActions = () =>
  useBattlePassStoreBase((state) => state.actions)
export const useBattlePassState = () =>
  useBattlePassStoreBase((state) => state.data)
export const useBattlePassLevels = () =>
  useBattlePassStoreBase((state) => state.battlePassLevels)

export const useBattlePass = createSelectors(useBattlePassStoreBase)
